import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  History,
  Receipt,
  Sparkles,
  Users2,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate, formatINR, formatINRSigned } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { SelectField } from '../components/ui/Field';
import { Pagination } from '../components/people/Pagination';
import { GatedSection } from '../components/shared/GatedSection';
import {
  ReferralCard,
  ReferralEventStatusChip,
  WalletCard,
  WalletEntryTypeChip,
  WithdrawalMethodChip,
  WithdrawalRequestDrawer,
  WithdrawalStatusChip,
  REFERRAL_EVENT_STATUS_FILTERS,
  WALLET_ENTRY_TYPE_FILTERS,
  academicYearOptions,
  currentAcademicYear,
  type ReferralCode,
  type ReferralEvent,
  type ReferralEventStatus,
  type ReferralOwnerType,
  type WalletLedgerEntry,
  type WalletSummary,
  type WithdrawalRequest,
} from '../components/wallet';
import type { ChildSummary } from '../components/me/types';
import type { WalletEntryType } from '@bbl/validation';

export const Route = createFileRoute('/_authed/me/referrals')({
  component: MyReferralsPage,
});

interface ChildrenResponse {
  items: ChildSummary[];
}

interface ListResponseBody<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

const PAGE_SIZE = 20;

type RoleOwnership =
  | { kind: 'owner'; ownerType: ReferralOwnerType; studentId?: undefined }
  | { kind: 'parent'; ownerType: 'student'; studentId: string }
  | { kind: 'none' };

function MyReferralsPage() {
  const role = useAuthStore((s) => s.user?.primaryRole);
  const user = useAuthStore((s) => s.user);

  // Parents see a child switcher; teachers and students just see themselves.
  const childrenQuery = useQuery({
    queryKey: ['me', 'children'] as const,
    queryFn: ({ signal }) =>
      apiRequest<ChildrenResponse>('/api/me/children', { signal }),
    enabled: role === 'parent',
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  // When the children list lands and no child is selected, snap to the first.
  const firstChildId = childrenQuery.data?.items[0]?.id ?? null;
  useEffect(() => {
    if (role === 'parent' && !selectedChildId && firstChildId) {
      setSelectedChildId(firstChildId);
    }
  }, [role, selectedChildId, firstChildId]);

  const ownership: RoleOwnership = useMemo(() => {
    if (role === 'teacher') return { kind: 'owner', ownerType: 'teacher' };
    if (role === 'student') return { kind: 'owner', ownerType: 'student' };
    if (role === 'parent' && selectedChildId) {
      return { kind: 'parent', ownerType: 'student', studentId: selectedChildId };
    }
    return { kind: 'none' };
  }, [role, selectedChildId]);

  return (
    <ListShell
      title="Referrals & Wallet"
      description="Share your code, track confirmed referrals, and withdraw your wallet balance."
    >
      {role === 'parent' ? (
        <ChildSwitcher
          items={childrenQuery.data?.items ?? []}
          selectedId={selectedChildId}
          onSelect={setSelectedChildId}
          isLoading={childrenQuery.isLoading}
          isError={childrenQuery.isError}
        />
      ) : null}

      {ownership.kind === 'none' ? (
        role === 'parent' ? null : (
          <EmptyState
            title="This page is for teachers, students and parents"
            description="Use your dashboard for your role."
            icon={<Sparkles size={32} aria-hidden="true" />}
            action={
              <Link to="/dashboard">
                <Button variant="secondary">Back to dashboard</Button>
              </Link>
            }
          />
        )
      ) : (
        <OwnerSurface
          ownership={ownership}
          ownerDisplayName={
            ownership.kind === 'parent'
              ? childrenQuery.data?.items.find((c) => c.id === ownership.studentId)?.name
              : user?.displayName
          }
        />
      )}
    </ListShell>
  );
}

// -------------------------------------------------------------------------
// Child switcher (parent only)
// -------------------------------------------------------------------------

interface ChildSwitcherProps {
  /**
   * The parent's children (domain nouns, not React children) — named `items`
   * so the prop is not confused with JSX children, which `react/no-children-prop`
   * correctly flags when passed explicitly.
   */
  items: ReadonlyArray<ChildSummary>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  isLoading: boolean;
  isError: boolean;
}

function ChildSwitcher({
  items,
  selectedId,
  onSelect,
  isLoading,
  isError,
}: ChildSwitcherProps) {
  if (isLoading) return <Skeleton className="h-12 w-full" />;
  if (isError || items.length === 0) {
    return (
      <EmptyState
        title="No children linked to your household"
        description="If you believe this is wrong, please contact the academy office."
        icon={<Users2 size={32} aria-hidden="true" />}
      />
    );
  }
  if (items.length === 1) return null;
  return (
    <nav
      aria-label="Choose child"
      className="flex flex-wrap gap-2 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-2"
    >
      {items.map((child) => {
        const active = child.id === selectedId;
        return (
          <button
            key={child.id}
            type="button"
            onClick={() => onSelect(child.id)}
            aria-pressed={active}
            className={clsx(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
              active
                ? 'bg-gold-50 text-gold-900 dark:bg-gold-700/20 dark:text-gold-50'
                : 'text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-bg-elevated)]',
            )}
          >
            {child.name}
            <span className="ml-2 font-mono text-[10px] text-[color:var(--color-text-muted)]">
              {child.admissionNumber}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// -------------------------------------------------------------------------
// OwnerSurface — referral card + wallet card + tabs
// -------------------------------------------------------------------------

interface OwnerSurfaceProps {
  ownership: Extract<RoleOwnership, { kind: 'owner' | 'parent' }>;
  ownerDisplayName: string | undefined;
}

function OwnerSurface({ ownership, ownerDisplayName }: OwnerSurfaceProps) {
  const studentId = ownership.kind === 'parent' ? ownership.studentId : undefined;
  const ownerType = ownership.ownerType;
  const perspective = ownership.kind === 'parent' ? 'child' : 'self';

  // --- Referral code (lazy-mint on GET) ---
  const codeQuery = useQuery({
    queryKey: ['referrals', 'my-code', studentId ?? null] as const,
    queryFn: ({ signal }) =>
      apiRequest<ReferralCode>('/api/referrals/my-code', {
        signal,
        ...(studentId ? { query: { studentId } } : {}),
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  // --- Wallet summary ---
  const summaryQuery = useQuery({
    queryKey: ['wallet', 'balance', studentId ?? null] as const,
    queryFn: ({ signal }) =>
      apiRequest<WalletSummary>('/api/wallet', {
        signal,
        ...(studentId ? { query: { studentId } } : {}),
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  // --- My withdrawals (used for outstanding + the tab) ---
  const myWithdrawalsQuery = useQuery({
    queryKey: ['wallet', 'withdrawals', 'mine', studentId ?? null] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponseBody<WithdrawalRequest>>('/api/wallet/withdrawals', {
        signal,
        query: {
          page: 1,
          pageSize: 50,
          ...(studentId ? { studentId } : {}),
        },
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const outstandingPaise = useMemo(() => {
    const items = myWithdrawalsQuery.data?.items ?? [];
    return items
      .filter((r) => r.status === 'requested' || r.status === 'approved')
      .reduce((sum, r) => sum + r.requestedAmountPaise, 0);
  }, [myWithdrawalsQuery.data]);

  const latestCooldownUntil = useMemo(() => {
    const items = myWithdrawalsQuery.data?.items ?? [];
    const future = items
      .map((r) => r.cooldownUntil)
      .filter((s): s is string => Boolean(s));
    if (future.length === 0) return null;
    const max = future.reduce((acc, s) => (s > acc ? s : acc));
    return max;
  }, [myWithdrawalsQuery.data]);

  const confirmedCountThisYear = useMemo(() => {
    return summaryQuery.data?.capStatus.confirmedCount ?? 0;
  }, [summaryQuery.data]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Use the *referral* query as the gating canary — it's hit unconditionally
  // and returns 404 `feature_disabled` when the module flag is off.
  return (
    <GatedSection
      error={codeQuery.error ?? summaryQuery.error}
      title="Referrals & Wallet"
      description="Once the wallet & referrals module is enabled by the academy, you'll see your referral code, share links, and earnings here."
      onRetry={() => {
        void codeQuery.refetch();
        void summaryQuery.refetch();
        void myWithdrawalsQuery.refetch();
      }}
    >
      <div className="grid gap-5 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <ReferralCard
            code={codeQuery.data}
            ownerType={ownerType}
            studentId={studentId}
            perspective={perspective}
            isLoading={codeQuery.isLoading}
            ownerDisplayName={ownerDisplayName}
          />
        </div>
        <div className="lg:col-span-2">
          <WalletCard
            summary={summaryQuery.data}
            confirmedReferralsCount={confirmedCountThisYear}
            ownerType={ownerType}
            isLoading={summaryQuery.isLoading}
            outstandingPaise={outstandingPaise}
            cooldownUntil={latestCooldownUntil}
            onRequestWithdrawal={() => setDrawerOpen(true)}
          />
        </div>
      </div>

      <div className="mt-6">
        <Tabs
          ownerType={ownerType}
          studentId={studentId}
          myWithdrawals={myWithdrawalsQuery.data?.items ?? []}
          isWithdrawalsLoading={myWithdrawalsQuery.isLoading}
        />
      </div>

      <WithdrawalRequestDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ownerType={ownerType}
        availablePaise={Math.max(
          (summaryQuery.data?.balancePaise ?? 0) - outstandingPaise,
          0,
        )}
        studentId={studentId}
      />
    </GatedSection>
  );
}

// -------------------------------------------------------------------------
// Tabs: My referrals · Ledger · My withdrawals
// -------------------------------------------------------------------------

type TabKey = 'referrals' | 'ledger' | 'withdrawals';

const TAB_OPTIONS: ReadonlyArray<{ value: TabKey; label: string; icon: React.ReactNode }> = [
  { value: 'referrals', label: 'My referrals', icon: <Users2 size={14} aria-hidden="true" /> },
  { value: 'ledger', label: 'Wallet ledger', icon: <History size={14} aria-hidden="true" /> },
  { value: 'withdrawals', label: 'My withdrawals', icon: <Receipt size={14} aria-hidden="true" /> },
];

interface TabsProps {
  ownerType: ReferralOwnerType;
  studentId?: string | undefined;
  myWithdrawals: ReadonlyArray<WithdrawalRequest>;
  isWithdrawalsLoading: boolean;
}

function Tabs({ ownerType, studentId, myWithdrawals, isWithdrawalsLoading }: TabsProps) {
  const [tab, setTab] = useState<TabKey>('referrals');

  return (
    <section>
      <div
        role="tablist"
        aria-label="Wallet sections"
        className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
      >
        {TAB_OPTIONS.map((opt) => {
          const active = tab === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(opt.value)}
              className={clsx(
                'inline-flex items-center gap-2 rounded-t-md px-4 py-2 text-sm font-medium transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                active
                  ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                  : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
              )}
            >
              {opt.icon}
              {opt.label}
            </button>
          );
        })}
      </div>

      <div className="mt-4">
        {tab === 'referrals' ? (
          <MyReferralsTab studentId={studentId} ownerType={ownerType} />
        ) : null}
        {tab === 'ledger' ? <LedgerTab studentId={studentId} /> : null}
        {tab === 'withdrawals' ? (
          <MyWithdrawalsTab
            items={myWithdrawals}
            isLoading={isWithdrawalsLoading}
          />
        ) : null}
      </div>
    </section>
  );
}

// ----- My referrals tab --------------------------------------------------

interface MyReferralsTabProps {
  ownerType: ReferralOwnerType;
  studentId?: string | undefined;
}

function MyReferralsTab({ ownerType, studentId }: MyReferralsTabProps) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | ReferralEventStatus>('all');
  const yearOptions = useMemo(() => academicYearOptions(), []);
  const [yearFilter, setYearFilter] = useState<string>(currentAcademicYear());

  const params = useMemo(() => {
    const out: Record<string, string | number | undefined> = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (statusFilter !== 'all') out.status = statusFilter;
    if (yearFilter) out.academicYear = yearFilter;
    if (studentId) out.studentId = studentId;
    return out;
  }, [page, statusFilter, yearFilter, studentId]);

  const query = useQuery({
    queryKey: ['referrals', 'mine', params] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponseBody<ReferralEvent>>('/api/referrals/mine', {
        signal,
        query: params,
      }),
    placeholderData: (prev) => prev,
  });

  const rewardCopy = ownerType === 'teacher' ? '₹1,500' : '₹500';

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <SelectField
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as typeof statusFilter);
            setPage(1);
          }}
          options={REFERRAL_EVENT_STATUS_FILTERS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
        />
        <SelectField
          label="Academic year"
          value={yearFilter}
          onChange={(e) => {
            setYearFilter(e.target.value);
            setPage(1);
          }}
          options={yearOptions.map((y) => ({ value: y, label: y }))}
        />
      </div>

      {query.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : query.isError ? (
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Could not load your referrals.{' '}
          <button
            type="button"
            onClick={() => query.refetch()}
            className="text-gold-700 underline"
          >
            Retry
          </button>
        </p>
      ) : (query.data?.items ?? []).length === 0 ? (
        <EmptyState
          title="No referrals yet"
          description={`Share your code to earn ${rewardCopy} per confirmed signup.`}
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--color-surface)]">
                  <tr>
                    <Th>Student</Th>
                    <Th>Joined</Th>
                    <Th>Year</Th>
                    <Th>Status</Th>
                    <Th align="right">Reward</Th>
                  </tr>
                </thead>
                <tbody>
                  {(query.data?.items ?? []).map((evt) => {
                    const rewardPaise =
                      evt.status === 'confirmed'
                        ? ownerType === 'teacher'
                          ? 150_000
                          : 50_000
                        : 0;
                    return (
                      <tr
                        key={evt.id}
                        className="border-t border-[color:var(--color-border)]"
                      >
                        <td className="px-4 py-2 align-middle">
                          <p className="font-medium text-[color:var(--color-text)]">
                            {evt.referredStudentName ?? '—'}
                          </p>
                          <p className="font-mono text-xs text-[color:var(--color-text-muted)]">
                            via {evt.source.replace(/_/g, ' ')}
                          </p>
                        </td>
                        <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                          {formatDate(evt.enteredAt)}
                        </td>
                        <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                          {evt.academicYear}
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <ReferralEventStatusChip status={evt.status} />
                        </td>
                        <td className="px-4 py-2 align-middle text-right tabular-nums">
                          {rewardPaise > 0 ? (
                            <span className="font-semibold text-[color:var(--color-success)]">
                              +{formatINR(rewardPaise)}
                            </span>
                          ) : (
                            <span className="text-[color:var(--color-text-muted)]">
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={query.data?.total ?? 0}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

// ----- Ledger tab --------------------------------------------------------

interface LedgerTabProps {
  studentId?: string | undefined;
}

function LedgerTab({ studentId }: LedgerTabProps) {
  const [page, setPage] = useState(1);
  const [entryType, setEntryType] = useState<'all' | WalletEntryType>('all');
  const yearOptions = useMemo(() => academicYearOptions(), []);
  const [yearFilter, setYearFilter] = useState<string>(currentAcademicYear());

  const params = useMemo(() => {
    const out: Record<string, string | number | undefined> = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (entryType !== 'all') out.entryType = entryType;
    if (yearFilter) out.academicYear = yearFilter;
    if (studentId) out.studentId = studentId;
    return out;
  }, [page, entryType, yearFilter, studentId]);

  const query = useQuery({
    queryKey: ['wallet', 'ledger', params] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponseBody<WalletLedgerEntry>>('/api/wallet/ledger', {
        signal,
        query: params,
      }),
    placeholderData: (prev) => prev,
  });

  // Compute the running balance client-side. The server returns newest-first;
  // we sort ascending, accumulate, then reverse to display newest-first with
  // the right cumulative figure on each row.
  const rowsWithBalance = useMemo(() => {
    const items = query.data?.items ?? [];
    if (items.length === 0) return [] as Array<WalletLedgerEntry & { runningBalance: number }>;
    const ascending = [...items].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    let balance = 0;
    const withBalance = ascending.map((entry) => {
      balance += entry.amountPaise;
      return { ...entry, runningBalance: balance };
    });
    return withBalance.reverse();
  }, [query.data]);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <SelectField
          label="Entry type"
          value={entryType}
          onChange={(e) => {
            setEntryType(e.target.value as typeof entryType);
            setPage(1);
          }}
          options={WALLET_ENTRY_TYPE_FILTERS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
        />
        <SelectField
          label="Academic year"
          value={yearFilter}
          onChange={(e) => {
            setYearFilter(e.target.value);
            setPage(1);
          }}
          options={yearOptions.map((y) => ({ value: y, label: y }))}
        />
      </div>

      {query.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : (query.data?.items ?? []).length === 0 ? (
        <EmptyState
          title="No transactions yet"
          description="Your ledger will fill up as referrals are confirmed and withdrawals are paid."
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--color-surface)]">
                  <tr>
                    <Th>When</Th>
                    <Th>Type</Th>
                    <Th align="right">Amount</Th>
                    <Th align="right">Balance</Th>
                    <Th>Notes</Th>
                  </tr>
                </thead>
                <tbody>
                  {rowsWithBalance.map((entry) => (
                    <tr key={entry.id} className="border-t border-[color:var(--color-border)]">
                      <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)] whitespace-nowrap">
                        {formatDate(entry.createdAt, { withTime: true })}
                      </td>
                      <td className="px-4 py-2 align-middle">
                        <WalletEntryTypeChip type={entry.entryType} />
                      </td>
                      <td className="px-4 py-2 align-middle text-right tabular-nums font-medium">
                        <span
                          className={clsx(
                            entry.amountPaise >= 0
                              ? 'text-[color:var(--color-success)]'
                              : 'text-[color:var(--color-text)]',
                          )}
                        >
                          {formatINRSigned(entry.amountPaise)}
                        </span>
                      </td>
                      <td className="px-4 py-2 align-middle text-right tabular-nums text-[color:var(--color-text-muted)]">
                        {formatINR(entry.runningBalance)}
                      </td>
                      <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                        {entry.notes ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={query.data?.total ?? 0}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}

// ----- My withdrawals tab ------------------------------------------------

interface MyWithdrawalsTabProps {
  items: ReadonlyArray<WithdrawalRequest>;
  isLoading: boolean;
}

function MyWithdrawalsTab({ items, isLoading }: MyWithdrawalsTabProps) {
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ ok: true }>(
        `/api/wallet/withdrawals/${encodeURIComponent(id)}/cancel`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Withdrawal cancelled.', { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['wallet'] });
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not cancel.', {
        variant: 'error',
      }),
  });

  const handleCancel = async (req: WithdrawalRequest) => {
    const ok = await confirm({
      title: 'Cancel this withdrawal request?',
      description:
        'The request will be marked cancelled and the amount returns to your available balance immediately. You can request another within this window.',
      confirmLabel: 'Cancel request',
      variant: 'danger',
    });
    if (!ok) return;
    cancelMutation.mutate(req.id);
  };

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (items.length === 0) {
    return (
      <EmptyState
        title="No withdrawal requests yet"
        description="When you request a payout, it will appear here with its lifecycle status."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)]">
            <tr>
              <Th>Requested</Th>
              <Th align="right">Amount</Th>
              <Th>Method</Th>
              <Th>Status</Th>
              <Th>Updated</Th>
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((req) => {
              const showCancel = req.status === 'requested';
              return (
                <tr key={req.id} className="border-t border-[color:var(--color-border)]">
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)] whitespace-nowrap">
                    {formatDate(req.requestedAt, { withTime: true })}
                  </td>
                  <td className="px-4 py-2 align-middle text-right tabular-nums font-semibold">
                    {formatINR(req.requestedAmountPaise)}
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <WithdrawalMethodChip method={req.method} />
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <WithdrawalStatusChip status={req.status} />
                    {req.rejectionReason ? (
                      <p className="mt-1 max-w-[20ch] truncate text-[11px] text-[color:var(--color-danger)]">
                        {req.rejectionReason}
                      </p>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)] whitespace-nowrap">
                    {req.paidAt
                      ? `Paid ${formatDate(req.paidAt)}`
                      : req.reviewedAt
                        ? `Reviewed ${formatDate(req.reviewedAt)}`
                        : '—'}
                  </td>
                  <td className="px-4 py-2 align-middle text-right">
                    {showCancel ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<XCircle size={14} aria-hidden="true" />}
                        onClick={() => handleCancel(req)}
                        loading={cancelMutation.isPending}
                      >
                        Cancel
                      </Button>
                    ) : (
                      <span className="text-xs text-[color:var(--color-text-muted)]">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Small helpers
// -------------------------------------------------------------------------

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={clsx(
        'px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

